import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Pill, Check, X, ImageIcon, Loader2, Phone, MapPin, Clock } from "lucide-react";

const STATUS_TINT = {
  pending: "bg-amber-500/10 text-amber-700",
  approved: "bg-emerald-500/10 text-emerald-700",
  out_for_delivery: "bg-indigo-500/10 text-indigo-700",
  completed: "bg-emerald-600/15 text-emerald-800",
  rejected: "bg-red-500/10 text-red-700",
  cancelled: "bg-neutral-500/10 text-neutral-700",
};

/**
 * Pharmacy provider inbox for incoming prescription photos.
 * Actions: approve (with price + optional pharmacist note) or reject (with reason).
 */
export const IncomingPrescriptions = ({ accent = "#22C55E", lang = "en" }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approveRxId, setApproveRxId] = useState(null); // id currently being priced
  const [price, setPrice] = useState("");
  const [pharmacistNote, setPharmacistNote] = useState("");
  const [rejectRxId, setRejectRxId] = useState(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [viewPhoto, setViewPhoto] = useState(null);

  const isAr = lang === "ar";

  const load = async () => {
    try {
      const { data } = await api.get("/prescriptions/incoming");
      setRows(data || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 6000);
    return () => clearInterval(iv);
  }, []);

  const submitApprove = async (rxId) => {
    if (!price || parseFloat(price) <= 0) return;
    setSubmitting(true);
    try {
      await api.post(`/prescriptions/${rxId}/approve`, {
        price: parseFloat(price),
        pharmacist_note: pharmacistNote || null,
      });
      setApproveRxId(null);
      setPrice("");
      setPharmacistNote("");
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to approve");
    } finally {
      setSubmitting(false);
    }
  };

  const submitReject = async (rxId) => {
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/prescriptions/${rxId}/reject`, { reason });
      setRejectRxId(null);
      setReason("");
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to reject");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="so-card p-5" data-testid="incoming-prescriptions">
      <div className="flex items-center gap-2 mb-3">
        <Pill className="w-5 h-5" style={{ color: accent }} />
        <div className="font-bold text-lg">{isAr ? "الوصفات الواردة" : "Incoming prescriptions"}</div>
        {rows.filter((r) => r.status === "pending").length > 0 && (
          <span className="ms-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white" style={{ background: accent }}>
            {rows.filter((r) => r.status === "pending").length} {isAr ? "بانتظار" : "pending"}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-[var(--so-text-soft)]"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-8 text-sm text-[var(--so-text-soft)]" data-testid="rx-empty">
          {isAr ? "لا توجد وصفات بعد. سيظهر هنا كل ما يرسله العملاء." : "No prescriptions yet. Anything customers send you will show up here."}
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((rx) => (
            <div key={rx.id} className="p-3 rounded-lg border border-[var(--so-border)]" data-testid={`rx-row-${rx.id}`}>
              <div className="flex gap-3">
                {/* Photo thumbnail */}
                <button
                  onClick={() => setViewPhoto(rx.photo_url)}
                  className="w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden flex-shrink-0 relative bg-neutral-200 dark:bg-neutral-800"
                  data-testid={`rx-photo-${rx.id}`}
                >
                  {rx.photo_url ? (
                    <img src={rx.photo_url} alt="Prescription" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="w-6 h-6 m-auto text-[var(--so-text-soft)]" />
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate">{rx.customer_name || "Customer"}</div>
                      <div className="text-[11px] text-[var(--so-text-soft)] flex items-center gap-1 truncate">
                        <MapPin className="w-3 h-3 flex-shrink-0" /> {rx.delivery_address}
                      </div>
                      {rx.contact_phone && (
                        <div className="text-[11px] text-[var(--so-text-soft)] flex items-center gap-1 mt-0.5" dir="ltr">
                          <Phone className="w-3 h-3" /> {rx.contact_phone}
                        </div>
                      )}
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_TINT[rx.status] || ""} flex-shrink-0`}>
                      {rx.status.replace("_", " ")}
                    </span>
                  </div>
                  {rx.note && (
                    <div className="mt-1.5 text-xs text-[var(--so-text)] bg-[var(--so-border)]/30 rounded p-1.5">
                      💬 {rx.note}
                    </div>
                  )}
                  <div className="text-[10px] text-[var(--so-text-soft)] flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3" /> {new Date(rx.created_at).toLocaleString()}
                  </div>
                  {rx.price != null && (
                    <div className="text-xs font-bold mt-1" style={{ color: accent }}>
                      {isAr ? "السعر:" : "Price:"} {rx.price.toLocaleString()} SDG
                    </div>
                  )}
                  {rx.reject_reason && (
                    <div className="text-xs text-red-500 mt-1">
                      ❗ {rx.reject_reason}
                    </div>
                  )}
                </div>
              </div>

              {/* Approve inline form */}
              {approveRxId === rx.id && (
                <div className="mt-3 p-3 rounded-lg border-2" style={{ borderColor: accent, background: `${accent}0A` }}>
                  <label className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>{isAr ? "أدخل السعر النهائي (SDG)" : "Set final price (SDG)"}</label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="0"
                    autoFocus
                    className="so-input mt-1"
                    data-testid={`rx-price-input-${rx.id}`}
                  />
                  <input
                    type="text"
                    value={pharmacistNote}
                    onChange={(e) => setPharmacistNote(e.target.value)}
                    placeholder={isAr ? "ملاحظة للعميل (اختياري)" : "Note to customer (optional)"}
                    className="so-input mt-2"
                    data-testid={`rx-pharm-note-${rx.id}`}
                  />
                  <div className="mt-2 flex gap-2">
                    <button onClick={() => { setApproveRxId(null); setPrice(""); setPharmacistNote(""); }} className="flex-1 px-3 py-1.5 rounded-lg text-sm font-semibold border border-[var(--so-border)]">
                      {isAr ? "إلغاء" : "Cancel"}
                    </button>
                    <button
                      onClick={() => submitApprove(rx.id)}
                      disabled={submitting || !price}
                      className="flex-1 px-3 py-1.5 rounded-lg text-sm font-bold text-white disabled:opacity-40 inline-flex items-center justify-center gap-1"
                      style={{ background: accent }}
                      data-testid={`rx-approve-confirm-${rx.id}`}
                    >
                      {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      {isAr ? "تأكيد" : "Approve"}
                    </button>
                  </div>
                </div>
              )}

              {/* Reject inline form */}
              {rejectRxId === rx.id && (
                <div className="mt-3 p-3 rounded-lg border-2 border-red-500/40 bg-red-500/5">
                  <label className="text-xs font-bold uppercase tracking-wider text-red-600">{isAr ? "سبب الرفض" : "Reason for decline"}</label>
                  <input
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={isAr ? "مثال: الدواء غير متوفر" : "e.g. Medicine out of stock"}
                    autoFocus
                    className="so-input mt-1"
                    data-testid={`rx-reason-input-${rx.id}`}
                  />
                  <div className="mt-2 flex gap-2">
                    <button onClick={() => { setRejectRxId(null); setReason(""); }} className="flex-1 px-3 py-1.5 rounded-lg text-sm font-semibold border border-[var(--so-border)]">
                      {isAr ? "إلغاء" : "Cancel"}
                    </button>
                    <button
                      onClick={() => submitReject(rx.id)}
                      disabled={submitting || !reason.trim()}
                      className="flex-1 px-3 py-1.5 rounded-lg text-sm font-bold text-white bg-red-500 disabled:opacity-40 inline-flex items-center justify-center gap-1"
                      data-testid={`rx-reject-confirm-${rx.id}`}
                    >
                      {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                      {isAr ? "تأكيد الرفض" : "Confirm decline"}
                    </button>
                  </div>
                </div>
              )}

              {/* Action buttons */}
              {rx.status === "pending" && approveRxId !== rx.id && rejectRxId !== rx.id && (
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => { setApproveRxId(rx.id); setRejectRxId(null); }}
                    className="flex-1 px-3 py-1.5 rounded-lg text-sm font-bold text-white inline-flex items-center justify-center gap-1"
                    style={{ background: accent }}
                    data-testid={`rx-approve-${rx.id}`}
                  >
                    <Check className="w-3.5 h-3.5" /> {isAr ? "موافقة" : "Approve"}
                  </button>
                  <button
                    onClick={() => { setRejectRxId(rx.id); setApproveRxId(null); }}
                    className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-red-500/40 text-red-500 hover:bg-red-500/5 inline-flex items-center justify-center gap-1"
                    data-testid={`rx-reject-${rx.id}`}
                  >
                    <X className="w-3.5 h-3.5" /> {isAr ? "رفض" : "Decline"}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Full-size photo viewer */}
      {viewPhoto && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setViewPhoto(null)} data-testid="rx-photo-viewer">
          <img src={viewPhoto} alt="Prescription full-size" className="max-w-full max-h-full rounded-lg" />
          <button className="absolute top-4 end-4 w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center" onClick={() => setViewPhoto(null)}>
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default IncomingPrescriptions;
